<template>
  <v-row>
    <v-col>
      <v-file-input
        :label="caption"
        accept=".jpg, .png"
        :multiple="multipleImages"
        :error="error"
        :hint="hint"
        :persistent-hint="!!hint"
        :error-messages="errorMessages"
        :hide-details="!hint"
        @change="onFileChange"
      />
      <!--
        `v-if` rather than `v-show`: closing a `closable` alert flips its own internal
        active state, and nothing resets it. A kept-alive instance would stay hidden for
        every later rejection, so each message mounts a fresh alert instead.

        One line per rejected file. A batch can fail for several reasons at once, and a
        single line could only ever report one of them.
      -->
      <v-alert
        v-if="rejectionMessages.length"
        type="error"
        class="mt-2"
        closable
      >
        <div
          v-for="(message, index) in rejectionMessages"
          :key="index"
        >
          {{ message }}
        </div>
      </v-alert>
      <v-alert
        v-if="warningMessages.length"
        type="warning"
        class="mt-2"
      >
        <div
          v-for="(message, index) in warningMessages"
          :key="index"
        >
          {{ message }}
        </div>
      </v-alert>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import type { ImageValidationOutcome, ValidatableImage } from './useImageValidation';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = withDefaults(defineProps<{
  onUploadImage: (_images: UploadedImage[]) => void;
  caption?: string;
  hint?: string;
  multipleImages?: boolean;
  onUploadImageGuard: (_image: ValidatableImage) => ImageValidationOutcome;
  errorMessages?: string | string[];
  error?: boolean;
}>(), {
  caption: () => useI18n().t('global.browse'),
  multipleImages: () => false,
});

const { t } = useI18n();

const rejectionMessages = ref<string[]>([]);
const warningMessages = ref<string[]>([]);

/** One file read and decoded far enough to measure. */
interface LoadedImage {
  data: string;
  width: number;
  height: number;
}

/**
 * Reads one file into a data URL and decodes it, so its dimensions can be measured.
 *
 * Resolves to `null` when the file is not an image, cannot be read, or cannot be
 * decoded. `accept` is a hint the file picker is free to ignore and drag-and-drop
 * bypasses entirely, so this is reachable; the caller reports it as a rejection rather
 * than letting the file disappear without a word.
 */
function loadImage(file: File): Promise<LoadedImage | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = (event) => {
      const data = event.target?.result;
      if (typeof data !== 'string' || !data.startsWith('data:image')) {
        resolve(null);
        return;
      }
      const img = new Image();
      img.onerror = () => resolve(null);
      img.onload = () => resolve({ data, width: img.width, height: img.height });
      img.src = data;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Names the file a message is about, but only when more than one was chosen - with a
 * single file the name adds nothing to a sentence already about that file.
 */
function describe(file: File, message: string, batched: boolean): string {
  return batched ? `${file.name}: ${message}` : message;
}

/**
 * Reads every chosen file, then hands the accepted ones over in one call.
 *
 * A rejected file no longer takes the rest of the batch down with it: each file is
 * judged on its own, the valid ones are uploaded, and every rejection is reported with
 * the name of the file it refers to. Results are placed by the order the files were
 * chosen in, not the order their reads happened to finish in.
 */
async function onFileChange(e: Event & { target: HTMLInputElement; dataTransfer?: DataTransfer }): Promise<void> {
  const fileList = e.target.files || e.dataTransfer?.files;
  if (!fileList?.length) {
    return;
  }

  rejectionMessages.value = [];
  warningMessages.value = [];

  const files = Array.from(fileList);
  const batched = files.length > 1;
  const loaded = await Promise.all(files.map(loadImage));

  const accepted: UploadedImage[] = [];
  const rejections: string[] = [];
  const warnings: string[] = [];

  loaded.forEach((image, index) => {
    const file = files[index];
    if (!image) {
      rejections.push(describe(file, t('images.validation.unreadable'), batched));
      return;
    }

    // The size comes from the file rather than the element: an `HTMLImageElement`
    // carries no size, so a guard given only the element could never apply a
    // maximum-size rule.
    const outcome = props.onUploadImageGuard?.({
      width: image.width,
      height: image.height,
      byteSize: file.size,
    });

    if (outcome?.hasError) {
      rejections.push(describe(file, outcome.errorMessage, batched));
      return;
    }
    if (outcome?.hasWarning) {
      warnings.push(describe(file, outcome.warningMessage, batched));
    }

    accepted.push({
      id: null,
      data: image.data,
      title: '',
    });
  });

  rejectionMessages.value = rejections;
  warningMessages.value = warnings;

  if (accepted.length) {
    props.onUploadImage(accepted);
  }
}
</script>
